'use client'

import Link from 'next/link'

import { useOptionalPageAnalytics } from '@/app/context/analytics'
import { NO_APLICA, NOT_SET } from '@/app/lib/analytics/constants'
import { slugify } from '@/app/lib/analytics/dataLayer'

import { BREADCRUMB_LABEL } from './PageIntro.constants'
import type { PageIntroCrumb } from './PageIntro.types'
import styles from './PageIntroParts.module.scss'

interface BreadcrumbProps {
  readonly items: ReadonlyArray<PageIntroCrumb>
}

/**
 * Breadcrumb interno compartido por ambas variantes de PageIntro.
 *
 * Semántica: `<nav>` etiquetado + lista ordenada para que los lectores de
 * pantalla anuncien la posición (WCAG 2.1 AA — 1.3.1). El separador `/` es
 * decorativo (`aria-hidden`). La última migaja marca la página actual con
 * `aria-current="page"`.
 */
export function Breadcrumb({ items }: BreadcrumbProps) {
  const analytics = useOptionalPageAnalytics()

  function handleClick(crumb: PageIntroCrumb, index: number) {
    analytics?.trackClick({
      click_type: 'breadcrumb',
      click_subtype: NO_APLICA,
      link_text: slugify(crumb.label),
      link_url: crumb.href ?? NO_APLICA,
      link_domain: NO_APLICA,
      module_name: NOT_SET,
      module_type: NO_APLICA,
      nav_level: String(index),
      nav_path: items
        .slice(0, index + 1)
        .map((item) => slugify(item.label))
        .join('/'),
      action_detail: NO_APLICA,
      action_value: NO_APLICA,
      action_context: NO_APLICA,
    })
  }

  return (
    <nav className={styles.breadcrumb} aria-label={BREADCRUMB_LABEL}>
      <ol className={styles.crumbList}>
        {items.map((crumb, index) => {
          const isLast = index === items.length - 1

          return (
            <li key={`${crumb.label}-${index}`} className={styles.crumbItem}>
              {crumb.href && !isLast ? (
                <Link
                  href={crumb.href}
                  className={styles.crumbLink}
                  onClick={() => handleClick(crumb, index)}
                >
                  {crumb.label}
                </Link>
              ) : (
                <span className={styles.crumbCurrent} aria-current={isLast ? 'page' : undefined}>
                  {crumb.label}
                </span>
              )}

              {!isLast && (
                <span className={styles.crumbSep} aria-hidden="true">
                  /
                </span>
              )}
            </li>
          )
        })}
      </ol>
    </nav>
  )
}
